import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";

import DetailBtn from "../../../component/atoms/button/detailBtn";
import Td from "../../../component/atoms/layout/table/td";
import TableText from "../../../component/atoms/text/table/tableText";
import Table from "../../../component/atoms/layout/table/table";
import Tr from "./../../../component/atoms/layout/table/tr";
import Th from "./../../../component/atoms/layout/table/th";
import Checkbox from "./../../../component/atoms/input/checkbox";
import MainLayout from "../../../component/atoms/layout/mainLayout";
import DetailPageLayout from "../../../component/atoms/layout/detail/detailPageLayout";
import PretendardText from "../../../component/atoms/text/pretendardText";
import ExcelBtn from "./../../../component/atoms/button/excelBtn";
import TableTitle from "./../../../component/atoms/text/table/tableTitle";
import TableTop from "./../../../component/atoms/layout/table/tableTop";
import SearchBar from "./../../../component/atoms/input/searchBar";
import GrayBtn from "./../../../component/atoms/button/grayBtn";
import RedBtn from "./../../../component/atoms/button/redBtn";

import Header from "../../../component/organisms/header";
import Sidebar from "../../../component/organisms/sidebar";

import TableCount from "./../../../component/molecules/tableCount";
import Pagination from "./../../../component/molecules/pagination";
import Empty from "./../../../component/molecules/empty";

import scrollToTop from "../../../util/scrollToTop";
import { formatNumberWithCommas } from "./../../../util/formatNumber";
import { toastMsg } from "../../../util/toastMsg";
import { errorHandler } from "../../../util/errorHandler";
import { route } from "./../../../router/route";

import Modal from "./../../../component/templates/Modal";
import BoardApi from "../../../api/board.api";
import moment from "moment";

// sessionStorage 초기화
function sessionStorageInit() {
  sessionStorage.removeItem("sCheckedListStorage");
  const event = new Event("sessionStorageChange");
  window.dispatchEvent(event);
}

const SecretQna = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams(); // eslint-disable-line no-unused-vars
  const [isWriteAuthority, setIsWriteAuthority] = useState(false);

  // ---------- 쓰기 권한체크 ---------- //
  useEffect(() => {
    const writeMenu = sessionStorage.getItem("writeMenu");

    if (writeMenu?.includes(route.secret_qna)) {
      setIsWriteAuthority(true);
    } else {
      setIsWriteAuthority(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [keyword, setKeyword] = useState("");
  const [activationFilter, setActivationFilter] = useState(true);

  const page = useMemo(() => {
    return searchParams.get("page") ? searchParams.get("page") : 1;
  }, [searchParams]);

  const [pagination, setPagination] = useState({
    size: 10, // 한 페이지에 표시될 게시글 수
    rangeSize: 5, // 하단 페이지 사이즈
    totalCount: 0,
    totalPages: 0,
    first: false,
    last: false,
  });

  // ---------- 마운트가 되면 ---------- //
  useEffect(() => {
    return () => {
      sessionStorage.removeItem("sCheckedListStorage");
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- 마운트가 되면 ---------- //
  useEffect(() => {
    setKeyword(searchParams.get("keyword") ? searchParams.get("keyword") : "");
    setActivationFilter(
      searchParams.get("activation") === "false" ? false : true
    );

    getQNAList();
  }, [location]); // eslint-disable-line react-hooks/exhaustive-deps

  const [qnaList, setQnaList] = useState([]);

  const getQNAList = async () => {
    try {
      const data = {
        page: Number(page),
        size: Number(pagination.size),
        boardPostType: "SECRET_QNA",
      };

      if (keyword) {
        data.keyword = searchParams.get("keyword");
      }

      if (searchParams.get("activation") === "false") {
        data.activation = "Inactive";
      }

      const response = (await BoardApi.getListBoard(data)).data.data;

      const processedResponse = [...response.content].map((item, index) => {
        return {
          ...item,
          numbering: response.totalCount - (page - 1) * pagination.size - index,
          checked: false,
        };
      });

      setQnaList(processedResponse);
      setPagination({
        ...pagination,
        totalCount: response.totalCount,
        totalPages: Math.ceil(response.totalCount / 10),
      });
    } catch (error) {
      errorHandler(error);
    }
  };

  // ---------- 체크 관련 ---------- //

  const sessionStorageCheckedList = sessionStorage.getItem(
    "sCheckedListStorage"
  );
  const [checkedListStorage, setCheckedListStorage] = useState([]);
  const [buttonGroupOpen, setButtonGroupOpen] = useState(false);
  const [isAllCheck, setIsAllCheck] = useState(false);

  useEffect(() => {
    const handleStorageChange = () => {
      setCheckedListStorage(
        sessionStorageCheckedList ? JSON.parse(sessionStorageCheckedList) : []
      );
    };

    window.addEventListener("sessionStorageChange", handleStorageChange);

    return () => {
      window.removeEventListener("sessionStorageChange", handleStorageChange);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    sessionStorage.setItem(
      "sCheckedListStorage",
      JSON.stringify(checkedListStorage)
    );
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (qnaList.length > 0) {
      const idsInArray1 = new Set(checkedListStorage.map((item) => item.id));

      const allIdsIncluded = qnaList.every((item) => idsInArray1.has(item.id));

      setIsAllCheck(allIdsIncluded);
    }

    if (checkedListStorage.length > 0) {
      setButtonGroupOpen(true);
    } else {
      setButtonGroupOpen(false);
    }
  }, [checkedListStorage, qnaList]); // eslint-disable-line react-hooks/exhaustive-deps

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const deleteHandler = async () => {
    try {
      for (let i = 0; i < checkedListStorage.length; i++) {
        await BoardApi.deleteBoard(checkedListStorage[i].id);
      }
      sessionStorageInit();
      setDeleteModalOpen(false);
      toast(toastMsg.delete);
      await getQNAList();

      navigate(
        `${route.secret_qna}?page=1&keyword=${keyword}&activation=${activationFilter}`
      );
    } catch (error) {
      errorHandler(error);
    }
  };

  // ---------- 활성 상태 관련 ---------- //
  const [filterOpen, setFilterOpen] = useState(false);

  const activationChangeFunc = async (value) => {
    try {
      const data = {
        activation: value,
      };

      for (let i = 0; i < checkedListStorage.length; i++) {
        await BoardApi.updateActivationBoard(checkedListStorage[i].id, data);
      }

      sessionStorageInit();
      toast(toastMsg.update);
      getQNAList();

      navigate(
        `${route.secret_qna}?page=1&keyword=${keyword}&activation=${activationFilter}`
      );
    } catch (error) {
      errorHandler(error);
    }
  };

  return (
    <>
      <Header />
      <MainLayout
        style={{
          position: "relative",
          gap: 306,
        }}
      >
        <Sidebar />

        {/* 본문 영역 */}
        <DetailPageLayout>
          <TableTitle>멤버십 QnA 관리</TableTitle>
          {/* 테이블 상단 영역 */}
          <TableTop>
            <TableCount text={"게시글"} count={pagination.totalCount} />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <SearchBar
                value={keyword}
                placeholder={"제목, 닉네임"}
                onChange={(e) => {
                  setKeyword(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    sessionStorageInit();
                    navigate(
                      `${route.secret_qna}?page=1&keyword=${keyword}&activation=`
                    );
                  }
                }}
                onClick={() => {
                  sessionStorageInit();
                  navigate(
                    `${route.secret_qna}?page=1&keyword=${keyword}&activation=`
                  );
                }}
              />
              {/* <ExcelBtn /> */}
            </div>
          </TableTop>

          {/* 버튼 그룹 */}
          {buttonGroupOpen && isWriteAuthority && (
            <div
              style={{
                width: "100%",
                height: 49,
                display: "flex",
                gap: 12,
              }}
            >
              {activationFilter ? (
                <GrayBtn
                  onClick={() => {
                    activationChangeFunc(false);
                  }}
                >
                  게시글 숨기기
                </GrayBtn>
              ) : (
                <GrayBtn
                  onClick={() => {
                    activationChangeFunc(true);
                  }}
                >
                  게시글 숨김 풀기
                </GrayBtn>
              )}

              <RedBtn
                style={{
                  backgroundColor: "#272829",
                }}
                onClick={() => {
                  setDeleteModalOpen(true);
                }}
              >
                삭제
              </RedBtn>
            </div>
          )}

          {/* 테이블 영역 */}
          <Table>
            <colgroup>
              {/* 합계: 1044px */}
              {/* 체크박스 */}
              <col width="56px" />
              {/* No */}
              <col width="40px" />
              {/* 제목 */}
              <col width="265px" />
              {/* 닉네임 */}
              <col width="237px" />
              {/* 등록일 */}
              <col width="122px" />
              {/* 조회수 */}
              <col width="72px" />
              {/* 댓글 */}
              <col width="59px" />
              {/* 상태 */}
              <col width="101px" />
              {/* 보기 */}
              <col width="92px" />
            </colgroup>
            <thead>
              <Tr style={{ height: "41px", background: "#272829" }}>
                <Th style={{ paddingLeft: "20px" }}>
                  <Checkbox
                    checked={isAllCheck}
                    onChange={(e) => {
                      if (e.target.checked) {
                        // 배열 병합 및 중복 제거
                        const mergedArray = [
                          ...qnaList,
                          ...checkedListStorage,
                        ].reduce((acc, current) => {
                          const existingItem = acc.find(
                            (item) => item.id === current.id
                          );
                          if (!existingItem) {
                            acc.push(current);
                          }
                          return acc;
                        }, []);

                        setCheckedListStorage(mergedArray);
                      } else {
                        const idsToExclude = new Set(
                          qnaList.map((item) => item.id)
                        );
                        const filteredArray = checkedListStorage.filter(
                          (item) => !idsToExclude.has(item.id)
                        );

                        setCheckedListStorage(filteredArray);
                      }
                    }}
                  />
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                    }}
                  >
                    No
                  </TableText>
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                    }}
                  >
                    제목
                  </TableText>
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                    }}
                  >
                    닉네임
                  </TableText>
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                    }}
                  >
                    등록일
                  </TableText>
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                    }}
                  >
                    조회수
                  </TableText>
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                    }}
                  >
                    댓글
                  </TableText>
                </Th>
                <Th>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      cursor: "pointer",
                      position: "relative",
                    }}
                  >
                    <TableText
                      style={{
                        fontWeight: "600",
                        lineHeight: "22.75px",
                      }}
                    >
                      상태
                    </TableText>
                    <img
                      className={filterOpen === true ? "rotate" : "rotate-back"}
                      src="/assets/admin/icons/ico_arrow_down.svg"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setFilterOpen(!filterOpen);
                      }}
                      alt="필터 펼치기/접기"
                    />
                    {filterOpen && (
                      <FilterWrapper>
                        <FilterTextWrapper>
                          <FilterText
                            onClick={() => {
                              navigate(
                                `${
                                  route.secret_qna
                                }?page=1&keyword=${keyword}&activation=${true}`
                              );
                            }}
                          >
                            활성
                            {activationFilter ? (
                              <img
                                src="/assets/admin/icons/ico_selectbox_checked.svg"
                                alt="선택됨"
                              />
                            ) : null}
                          </FilterText>
                        </FilterTextWrapper>
                        <FilterText
                          onClick={() => {
                            navigate(
                              `${
                                route.secret_qna
                              }?page=1&keyword=${keyword}&activation=${false}`
                            );
                          }}
                        >
                          숨김
                          {activationFilter ? null : (
                            <img
                              src="/assets/admin/icons/ico_selectbox_checked.svg"
                              alt="선택됨"
                            />
                          )}
                        </FilterText>
                      </FilterWrapper>
                    )}
                  </div>
                </Th>
                <Th>
                  <TableText
                    style={{
                      fontWeight: "600",
                      lineHeight: "22.75px",
                    }}
                  >
                    보기
                  </TableText>
                </Th>
              </Tr>
            </thead>
            <tbody>
              {qnaList.map((item, index) => {
                return (
                  <Tr
                    key={index}
                    style={{
                      height: "59px",
                      background: "transparent",
                      borderBottom: "0.5px solid rgba(255, 255, 255, 0.12)",
                    }}
                  >
                    <Td style={{ paddingLeft: "20px" }}>
                      <Checkbox
                        checked={checkedListStorage.some(
                          (item02) => item02.id === item.id
                        )}
                        onChange={() => {
                          if (
                            checkedListStorage.length > 0 &&
                            checkedListStorage.some(
                              (item02) => item02.id === item.id
                            )
                          ) {
                            let temp = checkedListStorage.filter(
                              (item02) => item02.id !== item.id
                            );
                            setCheckedListStorage(temp);
                          } else {
                            setCheckedListStorage([
                              item,
                              ...checkedListStorage,
                            ]);
                          }
                        }}
                      />
                    </Td>
                    <Td>
                      <TableText>{item.numbering}</TableText>
                    </Td>
                    {/* 제목 */}
                    <Td>
                      <TableText
                        style={{
                          maxWidth: "248px",
                        }}
                      >
                        {item.title}
                      </TableText>
                    </Td>
                    {/* 닉네임 */}
                    <Td>
                      <TableText
                        style={{
                          maxWidth: "98px",
                        }}
                      >
                        {item.member?.nickname ? item.member?.nickname : ""}
                      </TableText>
                    </Td>
                    {/* 등록일 */}
                    <Td>
                      <TableText>
                        {moment(item.createdAt).format("YYYY-MM-DD")}
                      </TableText>
                    </Td>
                    {/* 조회수 */}
                    <Td>
                      <TableText>
                        {formatNumberWithCommas(item.totalViewCount)}
                      </TableText>
                    </Td>
                    {/* 댓글 */}
                    <Td>
                      <TableText>
                        {formatNumberWithCommas(item.commentCount)}
                      </TableText>
                    </Td>
                    {/* 상태 */}
                    <Td>
                      <TableText
                        style={{
                          color: `${item.activation ? "#aaa" : "#cf6679"}`,
                        }}
                      >
                        {item.activation ? "활성" : "숨김"}
                      </TableText>
                    </Td>
                    {/* 보기 */}
                    <Td>
                      <DetailBtn
                        onClick={() => {
                          navigate(`${route.secret_qna_detail}/${item.id}`);
                          scrollToTop();
                        }}
                      />
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>

          {qnaList.length > 0 ? null : <Empty />}

          {/* 페이지네이션 영역 */}
          <Pagination
            route={route.secret_qna}
            queryString={`keyword=${keyword}&activation=${activationFilter}`}
            currentPage={page}
            totalPages={pagination.totalPages}
            rangeSize={pagination.rangeSize}
            first={pagination.first}
            last={pagination.last}
          />
        </DetailPageLayout>
      </MainLayout>

      {deleteModalOpen && (
        <Modal
          type="negative"
          title="삭제하시겠습니까?"
          text={
            <>
              삭제된 데이터는 복구가 불가합니다.
              <br />
              그래도 삭제하시겠습니까?
            </>
          }
          setModalOpen={setDeleteModalOpen}
          onClick={deleteHandler}
        />
      )}
    </>
  );
};

export default SecretQna;

const FilterWrapper = styled.div.attrs((props) => {})`
  padding: 10px;
  border-radius: 3px;
  background: #272829;
  box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 48px;
  z-index: 5;
`;

const FilterTextWrapper = styled.div.attrs((props) => {})`
  display: flex;
  gap: 1px;
`;

const FilterText = styled(PretendardText).attrs((props) => {})`
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 10px;
  color: #aaa;
  font-size: 15px;
  font-weight: 500;
  line-height: 22px;
  border-radius: 3px;
  cursor: pointer;

  &:hover {
    background: #313233;
  }
`;
